// MACHINERY V2 (Stroje) — fleet card chip row + decommission detail (M2.2).
//
// The carried live-grid chrome that is neither identity nor telemetry: group
// pills, the S/N-missing warning, and the decommissioned state's chip + date +
// reason. Split out of FleetCard.tsx to keep both files under the 300-line cap.
//
// PARITY (data package §9):
//   - groupNames  -> coloured pills (live: RedesignedMachineCard only)
//   - serialNumber missing -> a warning, `machineSnMissing`
//   - isDecommissioned -> badge + `decommissionedOn {date}` + italic quoted
//     reason, 2-line clamped
//
// S/N-MISSING IS AMBER, NOT RED (deliberate v2 rebuild). The live cards paint
// this warning red (#F23E3E) and tint the vertical card blue. In the v2 design
// language `--red` means an ACTIVE FAULT — it is the alarm strip's colour — and
// a missing serial number is an incomplete record, not a machine fault.
// Amber keeps the signal without borrowing the alarm vocabulary.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { Machine } from "../../models/models";
import { groupColor } from "./fleetCardAtoms";

const chipBase: React.CSSProperties = {
  fontFamily: "var(--font-mono)", fontSize: 10, fontWeight: 700,
  letterSpacing: "0.06em", textTransform: "uppercase", borderRadius: 999,
  padding: "2px 8px", whiteSpace: "nowrap",
};

export interface FleetCardMetaProps {
  machine: Machine;
  snMissing: boolean;
  decommissioned: boolean;
}

export const FleetCardChips: React.FC<FleetCardMetaProps> = ({
  machine, snMissing, decommissioned,
}) => {
  const { translate } = useTranslation("myMachinery");
  const groups = machine.groupNames || [];
  if (groups.length === 0 && !snMissing && !decommissioned) return null;

  return (
    <div
      style={{
        margin: "0 16px 12px", display: "flex", flexWrap: "wrap",
        alignItems: "center", gap: 6, minWidth: 0,
      }}
    >
      {groups.map((name) => (
        <span
          key={name}
          style={{
            fontSize: 10.5, fontWeight: 500, color: groupColor(name),
            background: groupColor(name) + "14", borderRadius: 999,
            padding: "2px 8px", whiteSpace: "nowrap",
          }}
        >
          {name}
        </span>
      ))}
      {snMissing && (
        <span
          style={{
            ...chipBase,
            color: "var(--amber)",
            background: "color-mix(in oklab, var(--amber) 12%, var(--card))",
            border: "1px solid color-mix(in oklab, var(--amber) 34%, var(--border))",
          }}
        >
          {translate("myMachinery:machineSnMissing")}
        </span>
      )}
      {decommissioned && (
        <span
          style={{
            ...chipBase,
            color: "var(--faint)", background: "var(--card-2)",
            border: "1px dashed var(--border)",
          }}
        >
          {translate("myMachinery:decommissioned")}
        </span>
      )}
    </div>
  );
};

/** Date + reason. Rendered only in the decommissioned scope. */
export const FleetCardDecommissionNote: React.FC<{ machine: Machine }> = ({ machine }) => {
  const { translate, getCurrentLanguage } = useTranslation("myMachinery");
  if (!machine.decommissionedDate && !machine.decommissionReason) return null;

  return (
    <div
      style={{
        margin: "0 16px 12px", fontSize: 11.5, color: "var(--muted)",
        lineHeight: 1.45, minWidth: 0,
      }}
    >
      {machine.decommissionedDate && (
        <div style={{ fontFamily: "var(--font-mono)", fontSize: 10.5 }}>
          {translate("myMachinery:decommissionedOn", {
            date: new Date(machine.decommissionedDate).toLocaleDateString(
              getCurrentLanguage()
            ),
          })}
        </div>
      )}
      {machine.decommissionReason && (
        <div
          style={{
            fontStyle: "italic", display: "-webkit-box", WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical", overflow: "hidden",
          }}
        >
          {"„" + machine.decommissionReason + "“"}
        </div>
      )}
    </div>
  );
};

export default FleetCardChips;
